import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack, Typography, Box, useTheme, useMediaQuery } from '@mui/material'
import React from 'react'
import { AddressFormData } from '../../types'
import { useAddressForm } from 'src/Libs/installation-statement/hooks'
import FieldDisplay from 'src/Libs/installation-statement/components/Overview/FieldDisplay'
import countryTranslations from 'i18n-iso-countries'

/**
 * A component in the overview step of the installation statement to display the entered address data
 */
const Address: React.VFC<AddressFormData> = ({ companyAddress, customerAddress, deviatingAddress, installer, deviatingAddressEnabled }) => {
  const { t, selectedAppLanguage } = useCustomTranslation()
  const addressFormFields = useAddressForm()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const getCountryName = (countryCode?: string): string => {
    return countryCode ? countryTranslations.getName(countryCode, selectedAppLanguage) ?? '' : ''
  }

  return (
    <Stack direction={isMobile ? 'column' : 'row'} justifyContent="space-between">
      <Stack direction={isMobile ? 'column' : 'row'} gap={14}>
        <Box>
          <Typography variant="h5" sx={{ pb: 2 }}>
            {t('installation-statement.overview.address.customer-address')}
          </Typography>
          <Typography variant="subtitle2">
            <FieldDisplay fieldName={addressFormFields.customerAddressForm.firstName.label}>{`${customerAddress.firstName}`}</FieldDisplay>{' '}
            <FieldDisplay fieldName={addressFormFields.customerAddressForm.lastName.label}>{`${customerAddress.lastName}`}</FieldDisplay>
          </Typography>

          <Typography variant="subtitle2">
            <FieldDisplay
              fieldName={addressFormFields.customerAddressForm.addressAndHouseNumber.label}
            >{`${customerAddress.addressAndHouseNumber}`}</FieldDisplay>
          </Typography>
          <Typography variant="subtitle2">
            <FieldDisplay fieldName={addressFormFields.customerAddressForm.zipCode.label}>{`${customerAddress.zipCode}`}</FieldDisplay>{' '}
            <FieldDisplay fieldName={addressFormFields.customerAddressForm.city.label}>{`${customerAddress.city}`}</FieldDisplay>
          </Typography>

          <Typography variant="subtitle2">
            <FieldDisplay fieldName={addressFormFields.customerAddressForm.country.label}>{getCountryName(customerAddress.country)}</FieldDisplay>
          </Typography>
        </Box>
        {deviatingAddressEnabled && (
          <Box>
            <Typography variant="h5" sx={{ pb: 2 }}>
              {t('installation-statement.overview.address.deviating-address')}
            </Typography>
            <Typography variant="subtitle2">
              <FieldDisplay
                fieldName={addressFormFields.deviatingAddressForm.addressAndHouseNumber.label}
              >{`${deviatingAddress?.addressAndHouseNumber}`}</FieldDisplay>
            </Typography>
            <Typography variant="subtitle2">
              <FieldDisplay fieldName={addressFormFields.deviatingAddressForm.zipCode.label}>{deviatingAddress?.zipCode ?? ''}</FieldDisplay>{' '}
              <FieldDisplay fieldName={addressFormFields.deviatingAddressForm.city.label}>{deviatingAddress?.city ?? ''}</FieldDisplay>
            </Typography>
            <Typography variant="subtitle2">
              <FieldDisplay fieldName={addressFormFields.deviatingAddressForm.country.label}>
                {getCountryName(deviatingAddress?.country)}
              </FieldDisplay>
            </Typography>
          </Box>
        )}
      </Stack>
      <Stack
        spacing={2}
        sx={theme => ({ p: 4, pr: 16, backgroundColor: theme.palette.shades.shade04, borderRadius: `${theme.shape.borderRadius}px` })}
      >
        <Typography variant="h5">{t('common.company')}</Typography>
        <Box>
          <Typography variant="subtitle2">
            <FieldDisplay fieldName={addressFormFields.companyAddressForm.companyName.label}>{companyAddress.companyName}</FieldDisplay>
          </Typography>
          <Typography variant="subtitle2">
            <FieldDisplay fieldName={addressFormFields.companyAddressForm.addressAndHouseNumber.label}>
              {companyAddress.addressAndHouseNumber}
            </FieldDisplay>
          </Typography>
          <Typography variant="subtitle2">
            <FieldDisplay fieldName={addressFormFields.companyAddressForm.zipCode.label}>{companyAddress.zipCode}</FieldDisplay>{' '}
            <FieldDisplay fieldName={addressFormFields.companyAddressForm.city.label}>{companyAddress.city}</FieldDisplay>
          </Typography>
          <Typography variant="subtitle2">
            <FieldDisplay fieldName={addressFormFields.companyAddressForm.country.label}>{getCountryName(companyAddress.country)}</FieldDisplay>
          </Typography>
        </Box>
        <Typography variant="h5">{t('common.installer')}</Typography>
        <Box>
          <Typography variant="subtitle2">{`${installer?.firstName} ${installer?.lastName}`}</Typography>
        </Box>
      </Stack>
    </Stack>
  )
}

export default Address
